import type { QuotaStatus } from "@api/types";
import { AlertCircle, ArrowUp, BarChart3, Clock, Shield } from "lucide-react";
import { shouldShowUpgradePrompts } from "../config/features";
import { useAuth } from "../contexts/AuthContext";
import { useQuotaStatus } from "../hooks/useQuotaStatus";
import { useUserAccount } from "../hooks/useUserAccount";
import { formatFullDate, formatTimeUntil } from "../lib/utils";
import { QuotaUpgradePrompt } from "./QuotaUpgradePrompt";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Skeleton } from "./ui/skeleton";

interface QuotaStatusCardProps {
    className?: string;
}

function getQuotaStatus(used: number, limit: number) {
    const percentage = (used / limit) * 100;

    if (percentage >= 100) {
        return { variant: "destructive" as const, percentage };
    }
    if (percentage >= 80) {
        return { variant: "warning" as const, percentage };
    }
    return { variant: "default" as const, percentage };
}

function getUpgradePromptUrgency(quotaData: QuotaStatus): "exceeded" | "approaching" | "promote" | null {
    const isExceeded =
        quotaData.topics.used >= quotaData.topics.limit ||
        quotaData.stories.used >= quotaData.stories.limit ||
        quotaData.drawings.used >= quotaData.drawings.limit;

    const isApproaching =
        quotaData.topics.used >= quotaData.topics.limit * 0.8 ||
        quotaData.stories.used >= quotaData.stories.limit * 0.8 ||
        quotaData.drawings.used >= quotaData.drawings.limit * 0.8;

    if (isExceeded) {
        return "exceeded";
    } else if (isApproaching) {
        return "approaching";
    } else {
        return "promote";
    }
}

function QuotaProgressBar({
    percentage,
    variant,
}: {
    percentage: number;
    variant: "default" | "warning" | "destructive";
}) {
    const getBarColor = () => {
        switch (variant) {
            case "destructive":
                return "bg-destructive";
            case "warning":
                return "bg-warning";
            default:
                return "bg-success";
        }
    };

    return (
        <div className="w-full bg-primary/15 rounded-full h-2">
            <div
                className={`h-2 rounded-full transition-all duration-300 ${getBarColor()}`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
            />
        </div>
    );
}

function QuotaItem({ label, used, limit, tier }: { label: string; used: number; limit: number; tier: string }) {
    const status = getQuotaStatus(used, limit);
    const isExceeded = used >= limit;
    const shouldShowUpgrade = tier === "free" && isExceeded && shouldShowUpgradePrompts();

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{label}</span>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                        {used}/{limit}
                    </span>
                    {shouldShowUpgrade && <ArrowUp className="h-3 w-3 text-primary" />}
                </div>
            </div>
            <QuotaProgressBar percentage={status.percentage} variant={status.variant} />
        </div>
    );
}

function QuotaStatusSkeleton() {
    return (
        <div className="space-y-4">
            <div className="text-center py-2">
                <div className="flex justify-center mb-2">
                    <BarChart3 className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <div className="text-sm text-muted-foreground">Loading usage data...</div>
            </div>
            {["Topics", "Stories", "Drawings"].map((label, i) => (
                <div key={i} className="space-y-2">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-muted-foreground">{label}</span>
                        <Skeleton className="h-4 w-12" />
                    </div>
                    <Skeleton className="h-2 w-full" />
                </div>
            ))}
        </div>
    );
}

export function QuotaStatusCard({ className = "" }: QuotaStatusCardProps) {
    const { user } = useAuth();
    const { data: quotaData, isLoading, error } = useQuotaStatus();
    const { tier } = useUserAccount();

    if (!user) return null;

    // Don't show quota card for bypass users

    if (error) {
        return (
            <Card className={`border-destructive/20 bg-destructive/5 ${className}`}>
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm">Unable to load quota status</span>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const resetTime = quotaData?.resetDate ? formatFullDate(quotaData.resetDate) : "";

    const urgencyText = quotaData ? getUpgradePromptUrgency(quotaData) : undefined;

    const isFreeTier = tier === "free";

    return (
        <Card className={className}>
            <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2 text-2xl">
                        <BarChart3 className={`h-5 w-5 ${isFreeTier ? "text-primary" : "text-secondary"}`} />
                        Daily Usage
                    </div>
                    {tier && !isLoading && (
                        <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                                isFreeTier
                                    ? "bg-primary/20 text-primary border border-primary/20"
                                    : "bg-secondary/20 text-secondary border border-secondary/20"
                            }`}
                        >
                            {tier.toUpperCase()}
                        </span>
                    )}
                </CardTitle>
                {quotaData && (
                    <CardDescription className="flex items-start gap-1 sm:items-center">
                        <Clock className="h-3 w-3 mt-0.5 sm:mt-0 flex-shrink-0" />
                        <span className="text-xs sm:text-sm">
                            Resets {resetTime} ({formatTimeUntil(quotaData.resetDate)})
                        </span>
                    </CardDescription>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {quotaData?.bypassQuota && (
                    <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                        <Shield className="h-4 w-4 text-primary" />
                        <span className="text-sm text-primary font-medium">Quota Exempt - Unlimited Usage</span>
                    </div>
                )}
                {isLoading ? (
                    <QuotaStatusSkeleton />
                ) : quotaData ? (
                    <>
                        <QuotaItem
                            label="Topics"
                            used={quotaData.topics.used}
                            limit={quotaData.topics.limit}
                            tier={tier || "free"}
                        />
                        <QuotaItem
                            label="Stories"
                            used={quotaData.stories.used}
                            limit={quotaData.stories.limit}
                            tier={tier || "free"}
                        />
                        <QuotaItem
                            label="Drawings"
                            used={quotaData.drawings.used}
                            limit={quotaData.drawings.limit}
                            tier={tier || "free"}
                        />
                        {tier === "free" && shouldShowUpgradePrompts() && urgencyText && (
                            <QuotaUpgradePrompt variant="inline" urgency={urgencyText} />
                        )}
                    </>
                ) : (
                    <div className="text-center text-muted-foreground text-sm">No quota data available</div>
                )}
            </CardContent>
        </Card>
    );
}
